import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  STUDYGROUP_MEMBERSHIP_STATUS_MEMBER,
  STUDYGROUP_VISIBILITY_SEARCH_DISABLED,
} from "@/constants";
import { useCreateEventReminders } from "@/hooks/useCreateEventReminders";
import { useDeleteEventReminders } from "@/hooks/useDeleteEventReminders";
import { useDeleteReminderByUser } from "@/hooks/useDeleteReminderByUser";
import {
  type StudyGroupJoinResult,
  useEventStudyGroupAutoJoin,
} from "@/hooks/useEventStudyGroupAutoJoin";
import { useForceJoinStudyGroup } from "@/hooks/useForceJoinStudyGroup";
import { usePropagateReminders } from "@/hooks/usePropagateReminders";
import {
  PARTICIPATION_STATE_ACCEPTED,
  useRespondToEvent,
} from "@/hooks/useRespondToEvent";
import { useStudyGroupYourMembership } from "@/hooks/useStudyGroupYourMembership";
import { toast } from "@/hooks/useToast";
import { useUpdateAllParticipantsReminders } from "@/hooks/useUpdateAllParticipantsReminders";
import type { EventApiResponse, UserShort } from "@/lib/humhub-api/types";
import { dedupeReminderItems } from "@/lib/utils/event/form-initializer";
import { useGlobalUserInfo } from "@/providers/AuthProvider";
import { useEventAttendAction } from "./useEventAttendAction";
import { useEventParticipantsActions } from "./useEventParticipantsActions";

interface UseEventAttendanceArgs {
  event: EventApiResponse;
  onDataRefresh?: () => void;
  shouldFetchMembership?: boolean;
}

const buildMembershipContext = (
  event: EventApiResponse,
  isLoggedIn: boolean,
  shouldFetchMembershipOverride?: boolean,
) => {
  const isSearchDisabledGroup =
    event?.container?.visibility === STUDYGROUP_VISIBILITY_SEARCH_DISABLED;
  const shouldFetchMembership =
    shouldFetchMembershipOverride ??
    Boolean(
      isLoggedIn &&
        event?.container?.id != null &&
        (isSearchDisabledGroup || event.is_owner),
    );
  const studyGroupIdForMembership = shouldFetchMembership
    ? event.container.id
    : null;

  return {
    isSearchDisabledGroup,
    shouldFetchMembership,
    studyGroupIdForMembership,
  };
};

const PARTICIPANT_MESSAGES = {
  accept: {
    success: "eventManage.acceptAllMembersSuccess",
    error: "eventManage.acceptAllMembersFailed",
  },
  decline: {
    success: "eventManage.declineParticipantsSuccess",
    error: "eventManage.declineParticipantsFailed",
  },
};

const PARTICIPANT_MESSAGE_KEYS = {
  accept: PARTICIPANT_MESSAGES.accept,
  decline: PARTICIPANT_MESSAGES.decline,
};

const buildParticipantMessages = (translate: (key: string) => string) => ({
  accept: {
    success: translate(PARTICIPANT_MESSAGE_KEYS.accept.success),
    error: translate(PARTICIPANT_MESSAGE_KEYS.accept.error),
  },
  decline: {
    success: translate(PARTICIPANT_MESSAGE_KEYS.decline.success),
    error: translate(PARTICIPANT_MESSAGE_KEYS.decline.error),
  },
  titles: {
    success: translate("common.success"),
    error: translate("common.error"),
  },
});

export const useEventAttendance = ({
  event,
  onDataRefresh,
  shouldFetchMembership: shouldFetchMembershipOverride,
}: UseEventAttendanceArgs) => {
  const { t } = useTranslation();
  const userInfo = useGlobalUserInfo();
  const isLoggedIn = Boolean(userInfo);
  const queryClient = useQueryClient();
  const participantMessages = useMemo(() => buildParticipantMessages(t), [t]);
  const { isSearchDisabledGroup, studyGroupIdForMembership } =
    buildMembershipContext(event, isLoggedIn, shouldFetchMembershipOverride);

  const { handleForceJoin } = useForceJoinStudyGroup();

  const { handleAutoJoin } = useEventStudyGroupAutoJoin({
    onComplete: (result: StudyGroupJoinResult) => {
      if (!result.success) {
        if (result.error) {
          toast({
            title: t("common.error"),
            description: t("studyGroup.autoJoinFailed", {
              groupName: result.groupName,
            }),
            variant: "error",
          });
        }
        return;
      }

      switch (result.action) {
        case "joined":
          toast({
            title: t("common.success"),
            description: t("studyGroup.joinedGroupWithAccess", {
              groupName: result.groupName,
            }),
          });
          break;
        case "applied":
          toast({
            title: t("common.success"),
            description: t("studyGroup.appliedToGroupWithAccess", {
              groupName: result.groupName,
            }),
          });
          break;
        case "already_member":
        case "not_eligible":
          break;
      }
    },
  });

  const { createReminders } = useCreateEventReminders({});

  const { deleteReminders } = useDeleteEventReminders({
    onSuccess: () => {},
    onError: () => {
      toast({
        title: t("common.error"),
        description: t("eventManage.remindersDeletionFailed"),
        variant: "error",
      });
    },
  });
  const { deleteReminderByUser } = useDeleteReminderByUser({
    onError: () => {
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderDeleteFailed"),
        variant: "error",
      });
    },
  });
  const { propagateReminders } = usePropagateReminders();
  const { updateAllParticipantsReminders } =
    useUpdateAllParticipantsReminders();

  const {
    isLoading: respondLoading,
    error: respondError,
    respond,
  } = useRespondToEvent({
    onSuccess: (_eventId, respondType) => {
      if (respondType === PARTICIPATION_STATE_ACCEPTED) {
        toast({
          title: t("common.success"),
          description: t("eventManage.eventJoinSuccessful"),
        });
      } else {
        toast({
          title: t("common.success"),
          description: t("eventManage.eventLeaveSuccessful"),
        });
      }
    },
    onError: (_error) => {
      toast({
        title: t("common.error"),
        description: t("eventManage.eventJoinFailed"),
        variant: "error",
      });
    },
  });

  const attendingParticipants = event?.participants?.attending || [];
  const uniqueReminders = useMemo(
    () => dedupeReminderItems(event?.reminders ?? []),
    [event?.reminders],
  );
  const uniqueReminderData = useMemo(
    () =>
      uniqueReminders.map((reminder) => ({
        unit: reminder.unit,
        value: reminder.value,
        type: reminder.type,
      })),
    [uniqueReminders],
  );
  const isAttending = useMemo(() => {
    if (!userInfo?.id) return false;
    return attendingParticipants.some(
      (attendee: UserShort) => String(attendee.id) === String(userInfo.id),
    );
  }, [attendingParticipants, userInfo?.id]);

  const {
    membershipRole,
    canManageMembers,
    isLoading: membershipLoading,
  } = useStudyGroupYourMembership(studyGroupIdForMembership);
  const isStudyGroupMember =
    membershipRole?.status === STUDYGROUP_MEMBERSHIP_STATUS_MEMBER;
  const {
    canBulkAcceptParticipants,
    handleAcceptParticipants,
    handleDeclineParticipants,
    handleAcceptAllParticipants,
    acceptAllParticipantsLoading,
    declineParticipantsLoading,
  } = useEventParticipantsActions({
    event,
    onDataRefresh,
    queryClient,
    t,
    uniqueReminders: uniqueReminderData,
    updateAllParticipantsReminders,
    propagateReminders,
    deleteReminderByUser,
    participantMessages,
  });
  const { handleAttendClick } = useEventAttendAction({
    event,
    isAttending,
    onDataRefresh,
    respond,
    t,
    uniqueReminderData,
    createReminders,
    deleteReminders,
    handleForceJoin,
    handleAutoJoin,
  });

  const canManageEvent = event?.is_owner || canManageMembers;
  const canShowAttendButton =
    isLoggedIn &&
    (!isSearchDisabledGroup || isStudyGroupMember || canManageEvent);
  const shouldRenderAttendCta =
    isAttending || (canShowAttendButton && !membershipLoading);

  return {
    isAttending,
    respondLoading,
    respondError,
    handleAttendClick,
    attendingParticipants,
    uniqueReminders,
    canManageEvent,
    shouldRenderAttendCta,
    handleAcceptAllParticipants,
    handleAcceptParticipants,
    handleDeclineParticipants,
    acceptAllParticipantsLoading,
    declineParticipantsLoading,
    canBulkAcceptParticipants,
  };
};
